export type StlJaw = 'upper' | 'lower' | 'other';
export type ViewerFileFormat = 'stl' | 'ply' | 'obj';
export type StlMatrix4 = [
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
];

export const STL_IDENTITY_MATRIX: StlMatrix4 = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];

export type StlLayerColor = 'model' | 'scan' | 'aestheticPlate';

export interface StlFileTypeConfig {
  suffix: string;
  color: StlLayerColor;
}

export const STL_FILE_TYPE_CONFIG = [
  { suffix: 'aestheticplate.stl', color: 'aestheticPlate' },
  { suffix: 'lowerjaw.stl', color: 'scan' },
  { suffix: 'upperjaw.stl', color: 'scan' },
  { suffix: '_cad.stl', color: 'model' },
] as const satisfies readonly StlFileTypeConfig[];

export interface StlLayoutLayer {
  id: string;
  name: string;
}

export interface StlLayerTransform {
  id: string;
  matrix: StlMatrix4;
}

export interface StlLayerLayoutOverride {
  fileName: string;
  jaw?: StlJaw;
  color?: StlLayerColor;
  matrix: StlMatrix4;
}

export interface StlJawLayoutConfig {
  matrix: StlMatrix4;
  hasConstructionInfoMatrix: boolean;
}

export interface StlLayoutConfig {
  sceneMatrix: StlMatrix4;
  hasConstructionInfoSceneMatrix: boolean;
  jaws: Record<StlJaw, StlJawLayoutConfig>;
  layerOverrides: readonly StlLayerLayoutOverride[];
}

export const STL_LAYOUT_CONFIG: StlLayoutConfig = {
  sceneMatrix: STL_IDENTITY_MATRIX,
  hasConstructionInfoSceneMatrix: false,
  layerOverrides: [],
  jaws: {
    upper: {
      matrix: STL_IDENTITY_MATRIX,
      hasConstructionInfoMatrix: false,
    },
    lower: {
      matrix: STL_IDENTITY_MATRIX,
      hasConstructionInfoMatrix: false,
    },
    other: {
      matrix: STL_IDENTITY_MATRIX,
      hasConstructionInfoMatrix: false,
    },
  },
};

export function getSupportedViewerFileFormat(name: string): ViewerFileFormat | undefined {
  const extension = fileNameKey(name).split('.').at(-1);
  return extension === 'stl' || extension === 'ply' || extension === 'obj' ? extension : undefined;
}

const UPPER_JAW_TOOTH_NUMBERS = new Set(
  Array.from({ length: 8 }, (_, index) => [`1${index + 1}`, `2${index + 1}`]).flat(),
);
const LOWER_JAW_TOOTH_NUMBERS = new Set(
  Array.from({ length: 8 }, (_, index) => [`3${index + 1}`, `4${index + 1}`]).flat(),
);

export function classifyStlJaw(name: string): StlJaw {
  const normalizedName = name.trim().toLocaleLowerCase('de');
  const normalizedFileName = normalizedName.replace(/[\s_-]+/g, '');
  const numericTokens = normalizedName.split(/[^0-9]+/).filter(Boolean);

  if (
    normalizedFileName.includes('upperjaw') ||
    normalizedFileName.includes('oberkiefer') ||
    normalizedFileName.includes('maxilla')
  ) {
    return 'upper';
  }

  if (
    normalizedFileName.includes('lowerjaw') ||
    normalizedFileName.includes('unterkiefer') ||
    normalizedFileName.includes('mandible') ||
    normalizedFileName.includes('mandibula')
  ) {
    return 'lower';
  }

  if (numericTokens.some((token) => UPPER_JAW_TOOTH_NUMBERS.has(token))) {
    return 'upper';
  }

  if (numericTokens.some((token) => LOWER_JAW_TOOTH_NUMBERS.has(token))) {
    return 'lower';
  }

  return 'other';
}

export function classifyStlJawFromToothNumbers(toothNumbers: readonly number[]): StlJaw {
  const hasUpperJawTooth = toothNumbers.some((toothNumber) =>
    UPPER_JAW_TOOTH_NUMBERS.has(String(toothNumber)),
  );
  const hasLowerJawTooth = toothNumbers.some((toothNumber) =>
    LOWER_JAW_TOOTH_NUMBERS.has(String(toothNumber)),
  );

  if (hasUpperJawTooth && !hasLowerJawTooth) {
    return 'upper';
  }

  if (hasLowerJawTooth && !hasUpperJawTooth) {
    return 'lower';
  }

  return 'other';
}

export function isStlAestheticPlateFile(name: string): boolean {
  return getStlFileTypeConfig(name)?.color === 'aestheticPlate';
}

export function isStlSupportedViewerFile(name: string, config: StlLayoutConfig = STL_LAYOUT_CONFIG): boolean {
  return (
    getSupportedViewerFileFormat(name) !== undefined &&
    (getStlFileTypeConfig(name) !== undefined || getStlLayerOverride(name, config) !== undefined)
  );
}

export function getStlLayerOverride(
  name: string,
  config: StlLayoutConfig,
): StlLayerLayoutOverride | undefined {
  return config.layerOverrides.find((override) => sameStlFileName(override.fileName, name));
}

export function getStlFileTypeConfig(name: string): StlFileTypeConfig | undefined {
  const format = getSupportedViewerFileFormat(name);
  if (format === undefined) {
    return undefined;
  }

  const normalizedName = fileNameKey(name);
  const fileNameWithoutExtension = normalizedName.slice(0, -(format.length + 1));

  return STL_FILE_TYPE_CONFIG.find((fileType) =>
    fileNameWithoutExtension.endsWith(fileType.suffix.slice(0, -'.stl'.length)),
  );
}

export function getStlLayerColor(
  name: string,
  modelColor: string,
  scanColor: string,
  aestheticPlateColor: string,
  layerColor?: StlLayerColor,
): string {
  const colorByType: Record<StlLayerColor, string> = {
    model: modelColor,
    scan: scanColor,
    aestheticPlate: aestheticPlateColor,
  };
  const configuredColor = layerColor ?? getStlFileTypeConfig(name)?.color;

  if (configuredColor) {
    return colorByType[configuredColor];
  }

  return modelColor;
}

export function layoutStlLayers(
  layers: readonly StlLayoutLayer[],
  config: StlLayoutConfig = STL_LAYOUT_CONFIG,
): StlLayerTransform[] {
  return layers.map((layer) => {
    // Exact ConstructionInfo filename matches outrank jaw-level fallbacks.
    const layerOverride = getStlLayerOverride(layer.name, config);
    const color = layerOverride?.color ?? getStlFileTypeConfig(layer.name)?.color;
    if (!layerOverride && (color === 'scan' || color === 'aestheticPlate')) {
      // Do not apply a denture jaw fallback to an unreferenced scan; see ../../README.md#design-review-stl-coordinates.
      return { id: layer.id, matrix: STL_IDENTITY_MATRIX };
    }
    const jaw = layerOverride?.jaw ?? classifyStlJaw(layer.name);
    const jawConfig = config.jaws[jaw];
    const usesConstructionInfoMatrix =
      config.hasConstructionInfoSceneMatrix ||
      layerOverride !== undefined ||
      jawConfig.hasConstructionInfoMatrix;

    if (usesConstructionInfoMatrix) {
      return {
        id: layer.id,
        matrix: matrixForLayer(
          layerOverride,
          jawConfig.hasConstructionInfoMatrix ? jawConfig.matrix : config.sceneMatrix,
        ),
      };
    }

    return {
      id: layer.id,
      matrix: STL_IDENTITY_MATRIX,
    };
  });
}

function sameStlFileName(left: string, right: string): boolean {
  return fileNameKey(left) !== '' && fileNameKey(left) === fileNameKey(right);
}

function fileNameKey(value: string): string {
  const normalized = value.trim().replaceAll('\\', '/');
  return normalized.slice(normalized.lastIndexOf('/') + 1).toLocaleLowerCase('de');
}

function matrixForLayer(layerOverride: StlLayerLayoutOverride | undefined, fallback: StlMatrix4): StlMatrix4 {
  return layerOverride === undefined ? fallback : layerOverride.matrix;
}
